import { createSelector } from 'redux-starter-kit';
import { RiskDescriptionsType } from 'store/modules/Meta/types';
import { PolicyType } from '../Policies/types';
import { prepareCustomerName } from 'utils';

const existingPoliciesSelector = (state: any) => state.existingPolicies;
const policiesSelector = (state: any) => state.policies;
const riskRequirementLevels = (state: any) => state.meta.riskRequirementLevels;
const riskCapacityLevels = (state: any) => state.meta.riskCapacity;
const forecastTypes = (state: any) => state.meta.returnForecastTypes;
const returnForecasts = (state: any) => state.meta.returnForecasts;
const riskDescriptionSelector = (state: any) => state.meta.riskDescriptions;
const riskLevels = (state: any) => state.meta.riskLevels;
const riskSummaries = (state: any) => state.riskSummaries;
const caseSelector = (state: any) => state.case;
const riskAssetAllocationSelector = (state: any) =>
  state.meta.riskAssetAllocations;
const downloadTypeSelector = (state: any) => state.download_type;
const capacityForLossSelector = (state: any) => state.capacityForLoss;

export const IPSSelectos = {
  download_type: createSelector(downloadTypeSelector, substate => substate),
  existingPolicies: createSelector(
    existingPoliciesSelector,
    subState => subState.results
  ),
  recommendedPolicies: createSelector(
    policiesSelector,
    subState => subState.results
  ),
  ongoingAdvisorCharges: createSelector(policiesSelector, subState => {
    const ongoingChargeType = 2;
    const policiesWithOngoingCharges = subState.results
      .filter(
        (policy: PolicyType) => policy.advisor_charges_definitions.length > 0
      )
      .map((policy: PolicyType) => ({
        ...policy,
        appliedAdvisorCharge: {
          ...policy.advisor_charges_definitions.filter(
            (chargeInstance: any) =>
              chargeInstance.charge_type === ongoingChargeType
          )[0]
        }
      }));
    return policiesWithOngoingCharges;
  }),
  initialAdvisorCharges: createSelector(policiesSelector, subState => {
    const initialChargeType = 1;
    const policiesWithOngoingCharges = subState.results
      .filter(
        (policy: PolicyType) => policy.advisor_charges_definitions.length > 0
      )
      .map((policy: PolicyType) => ({
        ...policy,
        appliedAdvisorCharge: {
          ...policy.advisor_charges_definitions.filter(
            (chargeInstance: any) =>
              chargeInstance.charge_type === initialChargeType
          )[0]
        }
      }));
    return policiesWithOngoingCharges;
  }),
  riskSummary: createSelector(
    riskRequirementLevels,
    riskCapacityLevels,
    caseSelector,
    capacityForLossSelector,
    (
      riskRequirementLevels,
      riskCapacityLevels,
      caseSelector,
      capacityForLossSelector
    ) => {
      if (!caseSelector.riskSummaries.results[0]) return [];
      let data = [];
      const directCustomersRiskTolerance =
        caseSelector.riskSummaries.results.filter(
          (riskSummary: any) => !riskSummary.customer.is_jointed
        );
      const {
        required_return,
        risk_tolerance,
        risk_capacity,
        recommended_risk_profile,
        time_horizon_interval
      } = directCustomersRiskTolerance[0];

      const { customers } = caseSelector;
      const isMoreThanOneCustomer = customers.length > 1;

      let filteredRequiredReturn = null;

      const roundedReturn = (Number(required_return) * 100).toFixed(2);

      if (required_return) {
        filteredRequiredReturn = riskRequirementLevels[
          time_horizon_interval
        ].find((level: any) => {
          const requiredReturn = Number(roundedReturn).toFixed(1);
          const maxLevel = (level.max * 100).toFixed(4);
          const minLevel = (level.min * 100).toFixed(4);

          return (
            Number(requiredReturn) >= Number(minLevel) &&
            Number(requiredReturn) <= Number(maxLevel)
          );
        });
      }

      const riskFormatted = Number(-risk_capacity * 100).toFixed(1);

      let filteredRiskCapacity = null;

      if (risk_capacity) {
        filteredRiskCapacity = riskCapacityLevels.find((level: any) => {
          const intRequiredReturn = Number(riskFormatted);
          const maxValue = -level.min * 100;
          const minValue = -level.max * 100;

          return intRequiredReturn >= minValue && intRequiredReturn <= maxValue;
        });
      }

      if (isMoreThanOneCustomer) {
        const customerOne = directCustomersRiskTolerance[0];
        const customerTwo = directCustomersRiskTolerance[1];
        data = [
          {
            name: 'Risk requirement',
            value: filteredRequiredReturn
              ? filteredRequiredReturn.risk_requirement_level
              : null
          },
          {
            name: `Risk tolerance ${prepareCustomerName(customerOne.customer)}`,
            value: customerOne.risk_tolerance
          },
          {
            name: `Risk tolerance ${prepareCustomerName(customerTwo.customer)}`,
            value: customerTwo.risk_tolerance
          },
          {
            name: 'Risk capacity',
            value: filteredRiskCapacity
              ? filteredRiskCapacity.risk_capacity_level
              : null
          },
          {
            name: 'Recommended risk profile',
            value: recommended_risk_profile
          }
        ];
      } else {
        data = [
          {
            name: 'Risk requirement',
            value: filteredRequiredReturn
              ? filteredRequiredReturn.risk_requirement_level
              : null
          },
          { name: 'Risk tolerance', value: risk_tolerance },
          {
            name: 'Risk capacity',
            value: filteredRiskCapacity
              ? filteredRiskCapacity.risk_capacity_level
              : null
          },
          {
            name: 'Recommended risk profile',
            value: recommended_risk_profile
          }
        ];
      }

      if (!capacityForLossSelector.config.includes(2)) {
        data = data.filter(
          value => !['Risk requirement', 'Risk capacity'].includes(value.name)
        );
      }

      return data;
    }
  ),
  forecastGrossInvestmentReturn: createSelector(
    caseSelector,
    forecastTypes,
    returnForecasts,
    (caseSelector, forecastTypes, returnForecasts) => {
      if (!caseSelector.riskSummaries.results[0]) return [];
      const { recommended_risk_profile, time_horizon_interval } =
        caseSelector.riskSummaries.results[0];

      const filteredData = returnForecasts.find((forecast: any) => {
        const riskLevel = Number(recommended_risk_profile);
        return (
          forecast.risk_level === riskLevel &&
          forecast.time_horizon === time_horizon_interval
        );
      });
      const findForecastName = (type: number) =>
        forecastTypes.find((forecastType: any) => {
          return Number(forecastType.value) === type;
        });

      const dataToDiplay = filteredData
        ? filteredData.forecasts.map((returnForecast: any) => {
            return {
              name: findForecastName(returnForecast.forecast_type).name,
              value: Number((Number(returnForecast.value) * 100).toFixed(2))
            };
          })
        : [];

      return dataToDiplay;
    }
  ),
  riskDescription: createSelector(
    riskDescriptionSelector,
    riskSummaries,
    riskLevels,
    (riskDescriptionSelector, riskSummaries, riskLevels) => {
      const parsedRiskDescriptions = riskDescriptionSelector.map(
        (risk: RiskDescriptionsType, index: number) => ({
          name: riskLevels[index].name,
          description: risk.description,
          risk_score: risk.risk_level
        })
      );

      const actualRisk =
        riskSummaries.results.length > 0 &&
        riskSummaries.results[0].recommended_risk_profile;

      const result = parsedRiskDescriptions.find((risk: any) => {
        return risk.risk_score === Math.floor(actualRisk);
      });
      return result;
    }
  ),
  caseName: createSelector(caseSelector, subState => subState.name),
  caseExecutiveSummary: createSelector(
    caseSelector,
    subState => subState.executiveSummary
  ),
  riskAllocations: createSelector(
    riskSummaries,
    riskAssetAllocationSelector,
    (riskSummaries, riskAssetAllocationSelector) => {
      const riskSummary =
        riskSummaries.results.length > 0 && riskSummaries.results[0];
      if (!riskSummary) return [];

      const { time_horizon_interval, recommended_risk_profile } = riskSummary;

      const filteredData = riskAssetAllocationSelector.filter((risk: any) => {
        const riskLevel = Number(recommended_risk_profile);
        return (
          risk.risk_level === riskLevel &&
          risk.time_horizon === time_horizon_interval
        );
      });

      return filteredData[0].allocations;
    }
  )
};
